/**
 * Tipos del panel "Recobros" (ms-ops) — roadmap §F2.b.
 *
 * ⚠️ FUENTE DE VERDAD: `ms-ops/packages/contracts/src/opsApi.ts`.
 *
 * Este fichero es un **espejo manual** de los contratos Zod de
 * `@ms-ops/contracts`. Se copia en lugar de importarse porque ms-ops es un
 * repo hermano fuera de este workspace CRA: no hay path mapping ni paquete
 * npm publicado que `react-scripts` pueda resolver. Si el backend cambia un
 * campo, este fichero se actualiza a mano — cualquier divergencia se nota en
 * runtime, no en compilación, así que conviene revisarlo al tocar la API.
 *
 * Endpoints cubiertos. El SPA NO llama a ms-ops directamente: el admin-api
 * hace de passthrough 1:1 bajo `${REACT_APP_BACKEND_URL}/api/admin/ops/*`,
 * que reenvía a `/v1/*` de ms-ops sin tocar métodos, query params ni bodies
 * (ver `src/api/msops.ts`). Las rutas de ms-ops, tal cual las define el
 * contrato, son:
 *   GET    /v1/episodes            (filtros + paginación)
 *   GET    /v1/episodes/:id        (detalle con snapshot/envíos/decisiones/notas)
 *   POST   /v1/episodes/:id/notes
 *   POST   /v1/episodes/:id/pause  · POST /v1/episodes/:id/resume
 *   GET    /v1/suppressions        · POST /v1/suppressions · DELETE /v1/suppressions/:id
 *   GET    /v1/decision-log        (feed del modo shadow)
 *   GET    /v1/cadences            · GET /v1/cadences/:id
 *
 * F2.d — acciones del panel y escritura de cadencias:
 *   POST   /v1/episodes/:id/send          (envío manual de una plantilla)
 *   GET    /v1/episodes/:id/payment-link  (links de pago Stripe por factura)
 *   GET    /v1/episodes/:id/otp           (link OTP del área de cliente)
 *   PUT    /v1/cadences/:id               (label / paused)
 *   PUT    /v1/cadences/:id/steps         (reemplazo del set + applyScope)
 */

// ---------------------------------------------------------------------------
// Paginación — una sola forma para TODOS los listados de la API.
// ---------------------------------------------------------------------------

/**
 * Envoltura de todo listado. Ojo: `page` de ms-ops es **1-based**, mientras
 * que el componente `Pagination` compartido de este SPA es 0-based. La
 * conversión se hace en la capa de vista, no aquí.
 */
export type Paginated<T> = {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
};

export const MSOPS_MAX_PAGE_SIZE = 200;

// ---------------------------------------------------------------------------
// Enums del dominio.
// ---------------------------------------------------------------------------

export type EpisodeStatus = "open" | "closed" | "paused";

export type ProcessKind = "dunning" | "renewal" | "signature" | "sepa_dispute";

export type ScheduledSendStatus = "pending" | "sent" | "canceled" | "failed";

/**
 * Conjunto ANCHO de mercados (el del email-orchestrator): los sub-mercados
 * españoles `AXA_WL_ES` / `SQL_ES` son canal broker (B2B2C) y colapsarlos a
 * `ES` perdería la bifurcación de a quién se comunica.
 */
export type MsOpsMarket = "ES" | "FR" | "IT" | "AXA_WL_ES" | "SQL_ES";

export const MSOPS_MARKETS: readonly MsOpsMarket[] = [
  "ES",
  "FR",
  "IT",
  "AXA_WL_ES",
  "SQL_ES",
];

export type MsOpsChannel =
  | "email"
  | "whatsapp"
  | "task"
  | "action"
  | "ai_call"
  | "sms";

/** Veredictos del motor — el eje del feed shadow. */
export type DecisionKind =
  | "send"
  | "shadow"
  | "skip"
  | "suppress"
  | "disputed"
  | "paused"
  | "exhausted"
  | "unsupported_channel"
  | "error";

export const DECISION_KINDS: readonly DecisionKind[] = [
  "send",
  "shadow",
  "skip",
  "suppress",
  "disputed",
  "paused",
  "exhausted",
  "unsupported_channel",
  "error",
];

// ---------------------------------------------------------------------------
// Episodios.
// ---------------------------------------------------------------------------

/**
 * Última comunicación del episodio (join con `scheduled_sends`). `null`
 * cuando aún no se ha programado nada. Se toma el envío más reciente por
 * `dueAt` sea cual sea su estado: a Ops le importa igual un `failed` (hay que
 * reintentarlo a mano) que un `sent`.
 */
export type LastCommunication = {
  id: string;
  channel: MsOpsChannel;
  status: ScheduledSendStatus;
  templateId: string | null;
  dueAt: string | null;
  sentAt: string | null;
};

export type EpisodeListItem = {
  id: string;
  processId: string;
  processKind: ProcessKind;
  subscriptionRef: string;
  subscriptionUuid: string | null;
  leadUlid: string | null;
  market: MsOpsMarket;
  status: EpisodeStatus;
  pendingInvoicesCount: number;
  /** `null` = D2C. Un valor = canal broker (B2B2C). */
  brokerId: string | null;
  disputed: boolean;
  currentStep: string | null;
  openedAt: string;
  closedAt: string | null;
  closeReason: string | null;
  lastReconciledAt: string | null;
  notesCount: number;
  lastCommunication: LastCommunication | null;
};

export type EpisodeNote = {
  id: string;
  episodeId: string;
  author: string;
  body: string;
  createdAt: string;
};

export type ScheduledSendItem = {
  id: string;
  episodeId: string;
  stepId: string | null;
  channel: MsOpsChannel;
  templateId: string | null;
  dueAt: string;
  status: ScheduledSendStatus;
  sentAt: string | null;
  canceledAt: string | null;
  failureReason: string | null;
  createdAt: string;
};

/**
 * Entrada del `decision_log`. `context` sólo viaja en el DETALLE del
 * episodio: el feed global lo omite a propósito porque puede arrastrar PII
 * del payload con el que se decidió.
 */
export type DecisionLogItem = {
  id: string;
  episodeId: string;
  stepId: string | null;
  decision: string;
  reason: string;
  dryRun: boolean;
  evaluatedAt: string;
  context?: Record<string, unknown>;
};

export type EpisodeDetail = EpisodeListItem & {
  /** Cadencia congelada al abrir el episodio (patrón CRM). Forma libre. */
  cadenceSnapshot: unknown;
  scheduledSends: ScheduledSendItem[];
  decisionLog: DecisionLogItem[];
  notes: EpisodeNote[];
};

// ---------------------------------------------------------------------------
// Queries.
// ---------------------------------------------------------------------------

export type EpisodeListQuery = {
  page?: number;
  pageSize?: number;
  processKind?: ProcessKind;
  status?: EpisodeStatus;
  market?: MsOpsMarket;
  /** Sólo episodios con >= N recibos pendientes — el eje real de la cadencia. */
  minReceipts?: number;
  /** Búsqueda libre: email o lead ULID. */
  q?: string;
  /** `true` = sólo disputados; `false` = sólo NO disputados; ausente = ambos. */
  disputed?: boolean;
};

export type DecisionLogQuery = {
  page?: number;
  pageSize?: number;
  verdict?: DecisionKind;
  /** Coincidencia parcial (ILIKE) sobre el motivo legible. */
  reason?: string;
  episodeId?: string;
  from?: string;
  to?: string;
};

export type SuppressionListQuery = {
  /** Filtro por dimensión — la pantalla Auto-recobro es kind=auto_retry. */
  kind?: SuppressionKind;
  page?: number;
  pageSize?: number;
  q?: string;
  /** `true` (default en el back) = sólo las vigentes. */
  active?: boolean;
};

// ---------------------------------------------------------------------------
// Bodies de escritura.
// ---------------------------------------------------------------------------

/** `author` NO viaja en el body: el back lo toma del JWT. */
export type CreateNoteBody = { body: string };

export type PauseEpisodeBody = {
  motivo: string;
  /** ISO-8601 con offset. Ausente = pausa indefinida. */
  hasta?: string;
  /** Ticket de HubSpot asociado. */
  ticket?: string;
};

export type PauseEpisodeResponse = {
  episodeId: string;
  status: EpisodeStatus;
  suppressionId: string | null;
};

export type ResumeEpisodeResponse = {
  episodeId: string;
  status: EpisodeStatus;
  revokedSuppressions: number;
};

// ---------------------------------------------------------------------------
// Supresiones.
// ---------------------------------------------------------------------------

export type SuppressionKind = "communications" | "auto_retry";

export type Suppression = {
  id: string;
  email: string | null;
  leadUlid: string | null;
  /**
   * Qué suprime: 'communications' (el Pausado clásico: no se le envía nada) o
   * 'auto_retry' (el switch "auto-recobro OFF": no se le reintenta el cobro
   * automático R6, pero los mensajes siguen). Ejes independientes.
   */
  kind: SuppressionKind;
  until: string | null;
  reason: string;
  ticket: string | null;
  createdBy: string;
  createdAt: string;
  revokedAt: string | null;
};

/** Identidad dual: email o leadUlid, al menos uno. */
export type CreateSuppressionBody = {
  kind?: SuppressionKind;
  email?: string;
  leadUlid?: string;
  reason: string;
  until?: string;
  ticket?: string;
};

// ---------------------------------------------------------------------------
// Cadencias (sólo lectura en F2.a).
// ---------------------------------------------------------------------------

/**
 * Estado DERIVADO de una cadencia (ms-ops §F3.b) — lo que Ops ve como badge.
 *
 * Existe porque "¿esto está encendido?" no tenía respuesta legible desde la
 * API: había que cruzar `cadences.paused` con `app_settings.dunning_gates`
 * (otra tabla) y con el hecho implícito de que una cadencia sin pasos es
 * inerte. Las tres cosas se veían igual: `paused: false`. El panel llegó a
 * pintar "Activa" para cadencias que NO enviaban nada — la clase de mentira
 * que hace pensar a un operador que hay comunicaciones saliendo a clientes.
 *
 *   - `off`    — no puede producir nada: sin pasos, o sin plantilla para
 *                ningún mercado (el motor los saltaría todos).
 *   - `paused` — kill-switch explícito de Ops (`cadences.paused`).
 *   - `shadow` — el motor decide y escribe en `decision_log`, pero NO envía:
 *                ningún mercado de la cadencia tiene el gate abierto.
 *   - `active` — al menos un mercado con el gate abierto ⇒ hay tráfico REAL.
 *
 * ⚠️ Es una lectura derivada, NO una palanca: la semántica de envío sigue
 * siendo `gates.byMarket[episode.market]`, evaluada episodio a episodio por el
 * motor. Se deriva en `@ms-ops/engine` (`deriveCadenceStatus`), al lado de
 * `decideNextStep`, para que las dos no puedan divergir en silencio.
 */
export type CadenceStatus = "off" | "paused" | "shadow" | "active";

export type CadenceListItem = {
  id: string;
  processId: string;
  processKind: ProcessKind;
  label: string;
  paused: boolean;
  /** `false` = sembrada por código; el editor no debe dejar romperla. */
  editable: boolean;
  stepCount: number;
  /**
   * Estado derivado. Viaja JUNTO a `paused` (no en su lugar): `paused` es el
   * dato crudo que el editor escribe con el `PUT`, `status` es la lectura para
   * humanos. Sustituir uno por otro obligaría a la UI a des-derivar el estado
   * para saber qué mandar en el toggle.
   */
  status: CadenceStatus;
  /** Frase EN CASTELLANO que explica el `status`. Se pinta tal cual. */
  statusReason: string;
  /** Mercados de la cadencia con el gate ABIERTO — envían de verdad. */
  marketsActive: MsOpsMarket[];
  /** Mercados de la cadencia con el gate CERRADO — deciden y registran. */
  marketsShadow: MsOpsMarket[];
  updatedAt: string;
  updatedBy: string | null;
};

export type CadenceStepItem = {
  id: string;
  cadenceId: string;
  position: number;
  channel: MsOpsChannel;
  actor: StepActor;
  /**
   * Disparador del paso. El contrato lo declara `unknown` en LECTURA (pasa el
   * jsonb tal cual), pero el editor necesita discriminarlo para pintar el
   * campo correcto: se estrecha con `parseTrigger()` antes de usarlo.
   */
  trigger: unknown;
  offsetMinutes: number;
  templateByMarket: Record<string, string>;
  templateVars: Record<string, unknown>;
  /** Sólo para `channel: 'action'`. `null` en los pasos de comunicación. */
  actionKind: ActionKind | null;
};

export type CadenceDetail = CadenceListItem & {
  steps: CadenceStepItem[];
};

export type CadenceListQuery = {
  page?: number;
  pageSize?: number;
  processKind?: ProcessKind;
  paused?: boolean;
};

// ---------------------------------------------------------------------------
// Etiquetas y estilos para la UI (castellano — es lo que lee Ops).
// ---------------------------------------------------------------------------

export const EPISODE_STATUS_LABEL: Record<EpisodeStatus, string> = {
  open: "Abierto",
  closed: "Cerrado",
  paused: "Pausado",
};

export const EPISODE_STATUS_STYLES: Record<
  EpisodeStatus,
  { bg: string; fg: string }
> = {
  open: { bg: "#fff3cd", fg: "#856404" },
  closed: { bg: "#d4edda", fg: "#155724" },
  paused: { bg: "#e9ecef", fg: "#495057" },
};

export const PROCESS_KIND_LABEL: Record<ProcessKind, string> = {
  dunning: "Impago",
  renewal: "Renovación",
  signature: "Firma",
  sepa_dispute: "Disputa SEPA",
};

export const CHANNEL_LABEL: Record<MsOpsChannel, string> = {
  email: "Email",
  whatsapp: "WhatsApp",
  task: "Tarea",
  action: "Acción",
  ai_call: "Llamada IA",
  sms: "SMS",
};

export const SEND_STATUS_LABEL: Record<ScheduledSendStatus, string> = {
  pending: "Pendiente",
  sent: "Enviado",
  canceled: "Cancelado",
  failed: "Fallido",
};

export const SEND_STATUS_STYLES: Record<
  ScheduledSendStatus,
  { bg: string; fg: string }
> = {
  pending: { bg: "#e7f1ff", fg: "#0069d9" },
  sent: { bg: "#d4edda", fg: "#155724" },
  canceled: { bg: "#e9ecef", fg: "#495057" },
  failed: { bg: "#fde2e2", fg: "#b32424" },
};

export const DECISION_LABEL: Record<DecisionKind, string> = {
  send: "Enviado",
  shadow: "Shadow",
  skip: "Sin paso elegible",
  suppress: "Suprimido",
  disputed: "En disputa",
  paused: "Pausado",
  exhausted: "Cadencia agotada",
  unsupported_channel: "Canal no soportado",
  error: "Error",
};

/**
 * Etiquetas del estado derivado de una cadencia.
 *
 * Están redactadas para que se lean SIN contexto: el fallo que corrigen es un
 * operador mirando el panel y concluyendo que salen comunicaciones cuando no
 * salen. Por eso `shadow` no dice "Shadow" a secas sino "no envía", y `active`
 * no dice "Activa" sino "enviando".
 */
export const CADENCE_STATUS_LABEL: Record<CadenceStatus, string> = {
  off: "Sin efecto",
  paused: "Pausada",
  shadow: "Modo shadow — no envía",
  active: "ACTIVA — enviando",
};

/**
 * Colores del estado derivado. La elección se aparta del semáforo habitual a
 * conciencia:
 *
 *   - `active` va en ROJO, no en verde. Verde se lee como "todo correcto" y
 *     aquí significa "hay comunicaciones reales saliendo a clientes": es el
 *     único estado que exige mirar dos veces antes de tocar nada.
 *   - `shadow` en azul, igual que el veredicto `shadow` del decision log, para
 *     que el mismo concepto tenga el mismo color en todo el panel. No es un
 *     fallo: es el estado esperado mientras los gates están cerrados.
 *   - `off` en gris apagado: no hay nada que mirar, y despausarla no cambiaría
 *     nada porque el problema es que está vacía.
 */
export const CADENCE_STATUS_STYLES: Record<
  CadenceStatus,
  { bg: string; fg: string }
> = {
  off: { bg: "#f8f9fb", fg: "#73879C" },
  paused: { bg: "#fff3cd", fg: "#856404" },
  shadow: { bg: "#e7f1ff", fg: "#0069d9" },
  active: { bg: "#fde2e2", fg: "#b32424" },
};

/**
 * `true` cuando NINGUNA de las cadencias puede enviar nada a un cliente.
 *
 * La condición es "ningún mercado con el gate abierto en ninguna cadencia", no
 * "todas en estado shadow": una cadencia `paused` o `off` tampoco envía, así
 * que exigir que todas estuvieran literalmente en `shadow` escondería el aviso
 * justo en el caso más inerte de todos. Con la lista vacía devuelve `false`:
 * sin datos no se afirma nada sobre el motor.
 */
export function allCadencesSilent(
  cadences: readonly Pick<CadenceListItem, "marketsActive">[],
): boolean {
  if (cadences.length === 0) return false;
  return cadences.every((c) => c.marketsActive.length === 0);
}

/**
 * Colores del veredicto. `shadow` en azul porque es el estado ESPERADO
 * mientras los gates de mercado están OFF — no es un fallo.
 */
export const DECISION_STYLES: Record<string, { bg: string; fg: string }> = {
  send: { bg: "#d4edda", fg: "#155724" },
  shadow: { bg: "#e7f1ff", fg: "#0069d9" },
  skip: { bg: "#f8f9fb", fg: "#73879C" },
  suppress: { bg: "#e9ecef", fg: "#495057" },
  disputed: { bg: "#fff3cd", fg: "#856404" },
  paused: { bg: "#e9ecef", fg: "#495057" },
  exhausted: { bg: "#f8f9fb", fg: "#73879C" },
  unsupported_channel: { bg: "#fff3cd", fg: "#856404" },
  error: { bg: "#fde2e2", fg: "#b32424" },
};

// ---------------------------------------------------------------------------
// F2.d — disparadores de un paso (StepTrigger, union discriminada).
//
// En LECTURA el contrato manda "trigger" como jsonb sin validar; en ESCRITURA
// (CadenceStepInput) lo valida contra la union de verdad. El editor tiene que
// discriminarlo para pintar el campo correcto, asi que se tipa aqui y se
// estrecha con parseTrigger(), que tolera un trigger desconocido en vez de
// romper la pantalla: el jsonb puede traer formas que este espejo no conozca.
// ---------------------------------------------------------------------------

export type StepActor = "auto" | "manual";

export type ActionKind = "recreate_invoice";

/** Se activa con N o mas recibos impagados. Cambia cuantos links de pago van. */
export type ReceiptsTrigger = { kind: "receipts"; threshold: number };

/** Se activa a los N dias LABORABLES desde la apertura del episodio. */
export type DaysTrigger = { kind: "days"; offsetDays: number };

/**
 * Se activa con N o mas disputas SEPA. Eje DISTINTO de receipts: no cambia el
 * numero de links sino la CONSECUENCIA contractual (1 = recordatorio,
 * 2 = poliza suspendida, 3 = anulacion).
 */
export type DisputesTrigger = { kind: "disputes"; threshold: number };

export type StepTrigger = ReceiptsTrigger | DaysTrigger | DisputesTrigger;

export type TriggerKind = StepTrigger["kind"];

export const TRIGGER_KINDS: readonly TriggerKind[] = [
  "receipts",
  "days",
  "disputes",
];

export const TRIGGER_KIND_LABEL: Record<TriggerKind, string> = {
  receipts: "Nº de recibos impagados",
  days: "Días laborables desde apertura",
  disputes: "Nº de disputas SEPA",
};

/**
 * Explicación en castellano de qué significa cada eje. Se pinta junto al
 * selector porque `receipts` y `disputes` se parecen en la forma (ambos son un
 * umbral) pero NO en la consecuencia, y confundirlos manda un aviso de
 * anulación a quien sólo debe un recibo.
 */
export const TRIGGER_KIND_HELP: Record<TriggerKind, string> = {
  receipts:
    "El paso se activa cuando la suscripción acumula al menos N recibos impagados.",
  days: "El paso se activa a los N días laborables desde que se abrió el episodio.",
  disputes:
    "El paso se activa con al menos N retrocesiones SEPA. Ojo: escala la consecuencia contractual (suspensión, anulación), no sólo el número de links.",
};

/**
 * Estrecha un `trigger` que llega como jsonb sin validar. Devuelve `null` si
 * no encaja en ninguna forma conocida, para que la UI pueda mostrarlo en crudo
 * en vez de reventar.
 */
export function parseTrigger(raw: unknown): StepTrigger | null {
  if (!raw || typeof raw !== "object") return null;
  const t = raw as Record<string, unknown>;
  if (t.kind === "receipts" && typeof t.threshold === "number") {
    return { kind: "receipts", threshold: t.threshold };
  }
  if (t.kind === "days" && typeof t.offsetDays === "number") {
    return { kind: "days", offsetDays: t.offsetDays };
  }
  if (t.kind === "disputes" && typeof t.threshold === "number") {
    return { kind: "disputes", threshold: t.threshold };
  }
  return null;
}

/** Resumen legible de un trigger para las tablas del editor. */
export function describeTrigger(raw: unknown): string {
  const trigger = parseTrigger(raw);
  if (!trigger) return "—";
  switch (trigger.kind) {
    case "receipts":
      return `≥ ${trigger.threshold} recibo(s) impagado(s)`;
    case "days":
      return `${trigger.offsetDays} día(s) laborable(s)`;
    case "disputes":
      return `≥ ${trigger.threshold} disputa(s) SEPA`;
    default:
      return "—";
  }
}

// ---------------------------------------------------------------------------
// F2.d — envío MANUAL (POST /v1/episodes/:id/send).
// ---------------------------------------------------------------------------

/**
 * Canal de un envío manual. Subconjunto ESTRICTO de `MsOpsChannel`: sólo los
 * que comunican con el cliente. `task`/`action` no comunican nada y
 * `ai_call`/`sms` están reservados sin implementación — ofrecerlos sería
 * prometer a Ops un botón que se traga el envío en silencio.
 */
export type ManualSendChannel = "email" | "whatsapp";

export const MANUAL_SEND_CHANNELS: readonly ManualSendChannel[] = [
  "email",
  "whatsapp",
];

/**
 * `templateId` es OBLIGATORIO y no hay campo de texto libre: es la regla dura
 * del roadmap §3 hecha FORMA del contrato. Email siempre vía
 * email-orchestrator con `storage-template` (saltárselo se salta la
 * comprobación fail-closed de supresiones) y WhatsApp siempre por plantilla de
 * waserver (un texto libre pone `isBot=false` y mata la automatización de esa
 * conversación para siempre, contrato irreversible de 2026-07-15).
 */
export type ManualSendBody = {
  channel: ManualSendChannel;
  /** gcsUri de storage-template (email) o id de plantilla Meta (WhatsApp). */
  templateId: string;
  variables?: Record<string, unknown>;
  /** `true` = simula y no envía. El gate del mercado puede forzarlo a `true`. */
  dryRun?: boolean;
};

/** Qué se HABRÍA enviado. Viaja igual en la simulación y en el envío real. */
export type ManualSendPreview = {
  channel: ManualSendChannel;
  templateId: string;
  market: MsOpsMarket;
  /** Destinatario resuelto, o `null` si el episodio no tiene identidad. */
  to: string | null;
  variables: Record<string, unknown>;
  /** `eventId` determinista con el que el receptor deduplicaría. */
  eventId: string;
};

/**
 * `simulated: true` llega con 200, no con un 4xx: cuando el gate del mercado
 * está OFF la verdad no es "has hecho algo mal" sino "este mercado todavía no
 * envía, y esto es lo que habría salido". La UI tiene que decirlo con esas
 * palabras.
 */
export type ManualSendResponse = {
  episodeId: string;
  /** `true` = NO se envió nada (gate OFF o `dryRun` explícito). */
  simulated: boolean;
  /** Por qué se simuló. `null` cuando el envío fue real. */
  simulatedReason: string | null;
  /** Id del `scheduled_sends` creado. `null` en simulación. */
  scheduledSendId: string | null;
  wouldSend: ManualSendPreview;
};

// ---------------------------------------------------------------------------
// F2.d — links de pago y OTP (paridad nº4: "copiar y pegar").
// ---------------------------------------------------------------------------

/**
 * Una factura con su link. `paymentUrl` puede ser `null` con `error` relleno:
 * una factura cuyo link no se resuelve NO invalida las demás — Ops trabaja con
 * clientes de 1, 2 y 3 recibos y un 502 global dejaría sin links los dos que
 * sí estaban.
 */
export type InvoicePaymentLink = {
  invoiceId: string;
  status: string | null;
  amountDueCents: number | null;
  currency: string | null;
  /** `hosted_invoice_url` de Stripe. `null` si no se pudo resolver. */
  paymentUrl: string | null;
  /** Motivo por el que falta el link. `null` cuando hay link. */
  error: string | null;
};

export type PaymentLinksResponse = {
  episodeId: string;
  leadUlid: string | null;
  subscriptionUuid: string | null;
  /** Recuento autoritativo de ms-payment, para contrastarlo con el episodio. */
  pendingInvoicesCount: number;
  links: InvoicePaymentLink[];
};

/**
 * `code` es opcional porque se extrae de la URL y el formato puede no
 * permitirlo: `null` es preferible a un código adivinado que Ops pegaría en un
 * WhatsApp.
 */
export type OtpLinkResponse = {
  episodeId: string;
  leadUlid: string;
  url: string;
  code: string | null;
};

// ---------------------------------------------------------------------------
// F2.d — escritura de cadencias (editor del panel).
// ---------------------------------------------------------------------------

/**
 * Alcance de una edición — patrón CRM.
 *
 *   - `new_only` (default): los episodios ya abiertos conservan su
 *     `cadence_snapshot` y siguen con el plan con el que entraron.
 *   - `waiting_too`: además re-sella el snapshot de los abiertos, que pasan a
 *     regirse por la cadencia nueva a mitad de camino.
 *
 * El default es el estrecho por daño asimétrico: cambiar el plan de miles de
 * episodios en vuelo no se deshace con un Ctrl+Z.
 */
export type ApplyScope = "new_only" | "waiting_too";

export const APPLY_SCOPES: readonly ApplyScope[] = ["new_only", "waiting_too"];

export const APPLY_SCOPE_LABEL: Record<ApplyScope, string> = {
  new_only: "Sólo episodios nuevos",
  waiting_too: "También los que están en curso",
};

/** Texto que la UI pinta junto a cada opción — Ops decide con esto delante. */
export const APPLY_SCOPE_HELP: Record<ApplyScope, string> = {
  new_only:
    "Los episodios ya abiertos siguen con el plan con el que entraron: su cadencia congelada no se toca. Sólo los episodios que se abran a partir de ahora usarán esta versión.",
  waiting_too:
    "Además de los nuevos, se re-sella la cadencia congelada de los episodios abiertos: cambian de plan a mitad de camino. No se puede deshacer — al guardar se indica a cuántos episodios ha afectado.",
};

export type UpdateCadenceBody = {
  label?: string;
  /** `true` congela la programación de nuevos pasos (kill-switch de Ops). */
  paused?: boolean;
};

/**
 * Un paso tal como lo manda el editor. `id` ausente = paso NUEVO; presente =
 * paso existente que se conserva (con su historial de envíos y las referencias
 * de los snapshots). Esa distinción es la que permite reordenar y editar sin
 * romper nada.
 */
export type CadenceStepInput = {
  id?: string;
  position: number;
  channel: MsOpsChannel;
  actor: StepActor;
  trigger: StepTrigger;
  offsetMinutes: number;
  /** Mercado ausente = el paso no aplica a ese mercado. */
  templateByMarket: Record<string, string>;
  templateVars: Record<string, unknown>;
  /** Sólo para `channel: 'action'`. */
  actionKind?: ActionKind;
};

/**
 * `PUT /v1/cadences/:id/steps` reemplaza el SET COMPLETO, no parchea paso a
 * paso: el editor manda el estado final y así dos operadores concurrentes no
 * acaban con una cadencia que ninguno de los dos diseñó.
 */
export type ReplaceCadenceStepsBody = {
  steps: CadenceStepInput[];
  applyScope: ApplyScope;
};

export type CadenceWriteResponse = {
  cadence: CadenceDetail;
  applyScope: ApplyScope;
  /** Nº de episodios abiertos cuyo `cadence_snapshot` se re-selló. */
  resyncedEpisodes: number;
};

// ---------------------------------------------------------------------------
// F2.d — errores de negocio del panel.
//
// ms-ops responde `{ error: <código>, message: <texto>, detail?: {…} }` con el
// status que le corresponde a cada motivo (ver `sendActionError` en
// `apps/api/src/modules/ops/routes.ts`). El panel NO se limita a pintar
// "HTTP 409": cada código tiene una consecuencia distinta para Ops —una
// supresión vigente se resuelve reanudando al cliente, una plantilla
// desconocida se resuelve eligiendo otra de la lista que el propio 422 trae—
// y el texto del back ya viene en castellano, así que se muestra tal cual.
// ---------------------------------------------------------------------------

export type MsOpsErrorBody = {
  error: string;
  message?: string;
  detail?: Record<string, unknown>;
};

/** Extrae el cuerpo de error de ms-ops de un `HttpError`, si lo trae. */
export function msOpsErrorBody(err: unknown): MsOpsErrorBody | null {
  const body = (err as { body?: unknown } | null)?.body;
  if (!body || typeof body !== "object") return null;
  const code = (body as Record<string, unknown>).error;
  if (typeof code !== "string") return null;
  return body as MsOpsErrorBody;
}

/**
 * Plantillas válidas que el 422 adjunta en `detail.available`. Es lo que
 * permite que el modal ofrezca la lista correcta en vez de obligar a Ops a
 * adivinar el gcsUri.
 */
export function availableTemplatesFromError(err: unknown): string[] {
  const detail = msOpsErrorBody(err)?.detail;
  const available = detail?.available;
  if (!Array.isArray(available)) return [];
  return available.filter((t): t is string => typeof t === "string");
}

/**
 * Qué hacer ante cada código, en lenguaje de Ops. El `message` del back
 * explica QUÉ pasó; esto añade el SIGUIENTE PASO, que es lo que falta cuando
 * alguien se queda mirando un error a las nueve de la mañana.
 */
export const MSOPS_ACTION_ERROR_HINT: Record<string, string> = {
  supresion_activa:
    "El cliente está silenciado por una supresión vigente. Si de verdad hay que escribirle, retira antes la supresión desde el episodio o desde Supresiones.",
  episodio_cerrado:
    "El impago ya está resuelto y el episodio cerrado: no se comunica sobre una deuda saldada.",
  plantilla_desconocida:
    "Esa plantilla no está declarada para este canal y mercado. Elige una de las que ofrece el desplegable.",
  plantilla_no_disponible_para_mercado:
    "La cadencia de este episodio no declara ninguna plantilla para este canal y mercado. Hay que añadirla en el editor de cadencias antes de poder enviar.",
  episodio_sin_lead:
    "El episodio no tiene lead identificado, así que no hay destinatario ni forma de generar el enlace.",
  canal_no_configurado:
    "Este canal no está configurado en el entorno. No es un error tuyo: falta la variable de entorno en ms-ops.",
  payment_no_configurado:
    "Los links de pago no están disponibles en este entorno (falta la conexión con ms-payment).",
  otp_no_configurado:
    "El OTP no está disponible en este entorno (falta la conexión con KITT).",
  payment_no_disponible:
    "ms-payment no respondió. Vuelve a intentarlo en un momento; si persiste, avisa a Tecnología.",
  otp_no_disponible:
    "No se pudo generar el OTP para este lead. Vuelve a intentarlo; si persiste, avisa a Tecnología.",
  envio_fallido:
    "El envío NO salió (el receptor lo rechazó o no respondió). La tarea ha vuelto a la cola y se puede reintentar: el eventId determinista evita duplicar si en realidad sí hubiera salido.",
  cadencia_no_editable:
    "Esta cadencia está sembrada por código y no se puede editar desde el panel.",
  pasos_en_uso:
    "Hay episodios abiertos que apuntan a los pasos que intentas borrar. Espera a que cierren, o conserva esos pasos (puedes editarlos, pero no eliminarlos).",
  posicion_duplicada: "Dos pasos comparten la misma posición. Revisa el orden.",
  paso_duplicado: "El mismo paso aparece dos veces en la lista.",
  paso_desconocido:
    "Uno de los pasos no pertenece a esta cadencia: recarga el editor, alguien la ha cambiado mientras trabajabas.",
  paso_sin_plantilla:
    "Un paso de comunicación no declara plantilla para ningún mercado: no enviaría nada.",
  accion_sin_tipo: "Un paso de tipo acción no declara qué acción ejecuta.",
  tipo_de_accion_en_canal_no_accion:
    "Sólo los pasos de canal 'Acción' pueden declarar un tipo de acción.",
  // --- Cola de tareas -----------------------------------------------------
  tarea_no_encontrada:
    "Esta tarea ya no existe. Recarga la cola: puede que otra persona la haya resuelto.",
  tarea_no_pendiente:
    "Otra operadora ya resolvió esta tarea (la envió o la descartó) mientras la tenías abierta. No se ha enviado nada por tu parte: recarga la cola y sigue con la siguiente.",
  variables_incompletas:
    "Faltan datos por rellenar. Un importe o un enlace sin sustituir en una mise en demeure es peor que no mandar el email, así que el envío se ha bloqueado entero.",
  episodio_en_disputa:
    "El episodio está en disputa: la cadencia se congela hasta resolverla y no debe salir ninguna comunicación.",
  canal_no_soportado:
    "La cola de tareas sólo envía email. Esta tarea es de otro canal y hay que resolverla desde el episodio.",
  // --- Envío real (incidente 2026-08-09) ----------------------------------
  destinatario_no_resoluble:
    "Falta el email del destinatario. Cópialo de la ficha del cliente (enlace de validación) y pégalo en el campo Destinatario: ms-ops no lo almacena y todavía no puede resolverlo solo.",
  plantilla_no_catalogada:
    "La plantilla de esta tarea no está en el catálogo de envío (TEMPLATE_CATALOG_JSON). No se ha enviado nada: avisa a tecnología para añadirla.",
  b2b2c_no_soportado:
    "Este episodio es de canal broker (B2B2C): la comunicación iría al broker y ese canal aún no está cableado. No se ha enviado nada.",
  paso_anterior_a_tarea:
    "Ese paso es anterior a la carta de esta tarea. El ajuste de fase es para declarar cartas POSTERIORES ya enviadas; si esta no procede, descártala con motivo.",
  fecha_invalida:
    "La fecha del envío externo no es válida (no puede estar en el futuro).",
  asunto_no_resoluble:
    "El asunto de la plantilla usa una variable sin valor (p.ej. el nº de póliza). No se ha enviado nada: revisa los datos de la tarea o avisa a tecnología.",
};

// ---------------------------------------------------------------------------
// Cola de TAREAS — `GET/POST /v1/tasks*`.
//
// Espejo de `ms-ops/packages/contracts/src/tasksApi.ts` (fuente de verdad).
//
// Una tarea es **un email ya preparado por el motor al que le faltan datos que
// ms-ops todavía no puede derivar, y que una operadora completa y envía**.
// Nace de un paso `actor: 'manual'` de una cadencia: el motor decide a quién y
// cuándo —respetando gates, supresiones, disputa y quiet hours exactamente
// igual que si fuese a enviar— y, en vez de enviar, materializa la tarea con
// todo lo que sabe precargado (`resolvedVariables`) y los huecos DECLARADOS
// explícitamente (`missingVariables`).
//
// Es lo que desbloquea Francia: el importe adeudado y el link de pago de
// Stripe no son derivables hasta que ms-payment absorba la emisión/consulta de
// Stripe. `missingVariables` es por tanto la medida exacta de lo que falta
// para automatizar — el día que quede vacía para un paso, ese paso puede pasar
// a `auto` sin rehacer la cadencia.
// ---------------------------------------------------------------------------

export type PendingTaskStatus = "pending" | "sent" | "dismissed";

export const PENDING_TASK_STATUSES: readonly PendingTaskStatus[] = [
  "pending",
  "sent",
  "dismissed",
];

export const PENDING_TASK_STATUS_LABEL: Record<PendingTaskStatus, string> = {
  pending: "Pendiente",
  sent: "Enviada",
  dismissed: "Descartada",
};

export const PENDING_TASK_STATUS_STYLES: Record<
  PendingTaskStatus,
  { bg: string; fg: string }
> = {
  pending: { bg: "#fff3cd", fg: "#856404" },
  sent: { bg: "#d4edda", fg: "#155724" },
  dismissed: { bg: "#e9ecef", fg: "#495057" },
};

/**
 * Fila de la cola. Lleva `missingVariables` pero **NO `resolvedVariables`**:
 * la lista pinta el badge "faltan 6 datos" sin traerse variables que son PII
 * (nombre, nº de póliza, últimos dígitos de la tarjeta) para 25 filas que no
 * las usan. Están en el detalle, que es donde se necesitan.
 */
export type TaskListItem = {
  id: string;
  episodeId: string;
  stepId: string;
  processId: string;
  processKind: ProcessKind;
  channel: MsOpsChannel;
  market: MsOpsMarket;
  templateId: string;
  status: PendingTaskStatus;
  /** Nombres de las variables que faltan por rellenar. */
  missingVariables: string[];
  dueAt: string;
  /** `true` si `dueAt` ya pasó y sigue `pending`. Derivado, no persistido. */
  overdue: boolean;
  assignee: string;
  completedBy: string | null;
  completedAt: string | null;
  dismissReason: string | null;
  scheduledSendId: string | null;
  createdAt: string;
  leadUlid: string | null;
  subscriptionRef: string;
};

/**
 * Detalle de una tarea: lo de la lista **más las variables ya resueltas**. Es
 * literalmente el formulario que la operadora rellena — `resolvedVariables` es
 * lo que ya está puesto (y puede revisar), `missingVariables` lo que tiene que
 * escribir. La pantalla no necesita nada más para construirse.
 */
export type TaskDetail = TaskListItem & {
  /**
   * Email del cliente resuelto AL VUELO desde ms-payment (R12). `null` =
   * no resoluble (el panel cae al campo manual). Nunca se persiste.
   */
  recipientEmail: string | null;
  /**
   * Balance del lead (R8 de ms-payment), resuelto al vuelo — `null` si no
   * responde. `alDia: true` con una tarea de impago delante = revisar antes
   * de enviar.
   */
  balance: {
    alDia: boolean;
    deudaCents: number;
    recibosPendientes: number;
    chargebackCents: number;
    disputasVivas: number;
    totalDueCents: number;
    collectionFeesCents: number;
    totalPayableCents: number;
    deudaDesde: string | null;
  } | null;
  /**
   * Método de pago con el que está fallando el cobro (P3). `last4` solo viene
   * si es TARJETA: en domiciliación el últimos-4 del IBAN no se presenta como
   * tarjeta, porque decírselo así al cliente sería falso.
   */
  paymentMethod: { type: string; last4: string | null } | null;
  /**
   * Valores sugeridos (formato FR ya listo) para las `missingVariables` de
   * dinero/fecha, derivados del balance. El panel los pre-rellena; la
   * operadora confirma o corrige.
   */
  suggestedVariables: Record<string, string>;
  /**
   * Pasos de comunicación de la cadencia del episodio (para el selector de
   * «ajustar fase»).
   */
  cadenceSteps: {
    id: string;
    position: number;
    channel: string;
    templateId: string | null;
  }[];
  /** Variables que ms-ops YA resolvió, congeladas al crear la tarea. */
  resolvedVariables: Record<string, unknown>;
  /**
   * Estado del episodio EN EL MOMENTO de la consulta. Va en el detalle (y no
   * sólo en la ficha del episodio) porque es la comprobación que evita el peor
   * fallo posible de esta cadencia: mandar una resiliación a alguien que pagó
   * ayer. El envío además lo rechaza con 409, pero eso es la red; esto es la
   * señal, en el mismo formulario donde se pulsa Enviar.
   */
  episodeStatus: EpisodeStatus;
  /**
   * Punteros a Stripe para validar el caso sin salir de la tarea: la cuenta
   * (Stripe FR es una cuenta SEPARADA — sin ella el dashboard abre la que no
   * es) y los ids de invoice de los recibos pendientes. Vacío = el episodio
   * aún no tiene artefacto de ms-payment: simplemente no se pinta el enlace.
   */
  stripe: { accountId: string | null; invoiceIds: string[] };
  /** `true` si el episodio está en disputa — la cadencia debería estar congelada. */
  episodeDisputed: boolean;
};

/**
 * Filtros de la cola. `status` por defecto `pending` **en el back**: una cola
 * existe para vaciarse y quien la abre quiere ver lo que le queda por hacer,
 * no el histórico. `overdue` es lo que la convierte en agenda ("¿qué se me ha
 * pasado?"), que es la pregunta que hoy responde HubSpot en FR.
 */
export type TaskListQuery = {
  page?: number;
  pageSize?: number;
  status?: PendingTaskStatus;
  market?: MsOpsMarket;
  processKind?: ProcessKind;
  overdue?: boolean;
  /** ISO-8601 con offset. */
  dueFrom?: string;
  dueTo?: string;
  assignee?: string;
  episodeId?: string;
  /** Búsqueda por nº de póliza o lead (el cliente llama diciendo su póliza). */
  q?: string;
  /** Estado del episodio. Default del back: `open` — lo pausado sale de la cola. */
  episodeStatus?: "open" | "paused" | "closed" | "all";
};

/**
 * Cuerpo del envío: **sólo las variables que faltaban**, por nombre.
 *
 * Sin `templateId` (la fijó la cadencia), sin texto libre (email SIEMPRE
 * storage-template vía orchestrator) y sin las ya resueltas (las congeló el
 * motor). La validación del back es TODAS o ninguna: 422 con la lista de las
 * que faltan si se omite cualquiera, y también si se mandan variables que la
 * tarea no declara.
 *
 * ⚠️ Los valores son **strings tal cual** — el contrato es
 * `z.record(z.string(), z.string().trim().min(1))`. Llegan a la plantilla SIN
 * reformatear: en FR los importes van en convención francesa preformateada
 * (`45,80`) y `debt_reference_date` en `DD/MM/AAAA`. El panel AVISA del formato
 * esperado pero NO transforma lo que escribe la operadora: reformatear o sumar
 * strings ya formateados es justo lo que produce un `45,8045,20` en una mise en
 * demeure (ver `ms_ops_francia_plantillas_pendientes.md` §3).
 */
export type CompleteTaskBody = {
  variables: Record<string, string>;
  /** `true` = valida y previsualiza sin enviar. */
  dryRun?: boolean;
  /**
   * Email del destinatario, copiado por la operadora de la ficha del cliente
   * (los deep-links de validación llevan justo ahí). ms-ops lo usa en el
   * momento del envío y NO lo persiste (PII de ms-identity): mientras
   * ms-payment no exponga el email en su by-lead (R12), sin este campo el
   * envío real responde 422 `destinatario_no_resoluble` sin tocar la tarea.
   */
  recipientEmail?: string;
};

/**
 * Qué se habría enviado. Se devuelve en la simulación Y en el envío real, para
 * que el panel enseñe exactamente lo mismo en ambos casos y Ops pueda comparar
 * antes de abrir el gate del mercado.
 */
export type TaskSendPreview = {
  templateId: string;
  market: MsOpsMarket;
  to: string | null;
  /** Resueltas + las que acaba de aportar la persona, ya fusionadas. */
  variables: Record<string, unknown>;
  /** `eventId` determinista con el que el orchestrator deduplicaría. */
  eventId: string;
  /** Remitente efectivo. En FR: `Equipe support de Life5 <support@life5.com>`. */
  sender: { from: string; replyTo?: string };
  bcc: string[];
};

/**
 * «Ajustar fase» — `POST /v1/tasks/:id/mark-external`. Para el stock que YA
 * estaba en proceso antes de ms-ops (relance/MED por Gmail, etapa en HubSpot):
 * la operadora declara hasta qué carta recibió el cliente y cuándo; ms-ops
 * registra esos pasos como hechos (con traza `origin: 'historical'`), ancla el
 * reloj de la cadencia a la fecha REAL y resuelve las tareas de esos pasos. El
 * siguiente escalón aflora en la próxima pasada del motor (~10 min).
 */
export type MarkTaskExternalBody = {
  /** `cadence_steps.id` del ÚLTIMO paso que el cliente ya recibió. */
  ultimoPasoEnviado: string;
  /** Fecha real de ese envío, `YYYY-MM-DD` (la del Gmail/HubSpot). */
  fechaEnvio: string;
  nota?: string;
};

export type MarkTaskExternalResponse = {
  taskId: string;
  episodeId: string;
  stepsMarked: string[];
  tasksResolved: string[];
  cadenceAnchorAt: string;
};

export type TaskSendResponse = {
  taskId: string;
  episodeId: string;
  status: PendingTaskStatus;
  /** `true` = NO se envió nada (gate del mercado OFF o `dryRun` explícito). */
  simulated: boolean;
  simulatedReason: string | null;
  /** Id del `scheduled_sends` creado. `null` en simulación. */
  scheduledSendId: string | null;
  wouldSend: TaskSendPreview;
};

/** Descarte con **motivo obligatorio**, en castellano como lo escribe Ops. */
export type DismissTaskBody = { motivo: string };

/** «Aplazar»: el cliente prometió pagar. Mueve el vencimiento, NO resuelve el paso. */
export type DeferTaskBody = { hasta: string; motivo: string };
export type DeferTaskResponse = { taskId: string; dueAt: string; motivo: string };

/** Dar por resuelta una disputa (salida del episodio congelado). */
export type ResolveDisputeBody = {
  desenlace: "ganada" | "perdida" | "reemitida" | "sin_disputa";
  motivo: string;
};
export type ResolveDisputeResponse = {
  episodeId: string;
  disputed: false;
  desenlace: string;
};

export type DismissTaskResponse = {
  taskId: string;
  status: PendingTaskStatus;
  dismissReason: string;
  completedBy: string;
  completedAt: string;
};

/**
 * Etiqueta legible + ayuda de formato de cada variable que Ops rellena a mano.
 *
 * Los textos NO son decorativos: el formato de estos campos es responsabilidad
 * del PRODUCTOR (aquí, la operadora) porque las plantillas no formatean nada.
 * Un `2026-07-20` donde la MED espera `20/07/2026`, o un `45.80` donde espera
 * `45,80`, sale tal cual en un acto jurídico.
 */
export const TASK_VARIABLE_HELP: Record<
  string,
  { label: string; help: string; placeholder?: string }
> = {
  card_last_digits: {
    label: "Últimos 4 dígitos de la tarjeta",
    help:
      "Los 4 dígitos de la tarjeta POR DEFECTO del cliente en Stripe (la que se cobrará). " +
      "Se rellena solo cuando ms-payment los conoce; si está vacío es que el método vive " +
      "solo en Stripe: ábrelo con «Stripe: factura» de arriba → cliente → Payment methods " +
      "y copia la marcada Default. Pedido a ms-payment que lo traiga solo (P3.b, 2026-08-08).",
    placeholder: "2923",
  },
  total_due_amount: {
    label: "Importe adeudado (principal)",
    help: "Suma de TODOS los recibos vencidos. Solo principal: sin gastos de recobro ni intereses. Convención francesa, coma decimal.",
    placeholder: "45,80",
  },
  collection_fees_amount: {
    label: "Gastos de recobro",
    help:
      "Solo para disputados: el cargo que Stripe cobra a Life5 por la disputa/devolución. " +
      "Hoy se consulta A MANO en el Dashboard de Stripe (la comisión de disputa del recibo). " +
      "Convención francesa, coma decimal. Decisión Yago 2026-08-05.",
    placeholder: "15,00",
  },
  total_payable_amount: {
    label: "Total a pagar",
    help: "Principal + gastos: la única cifra que el cliente ve. Se escribe entera a mano y NO se calcula sumando los otros dos, que van preformateados. Convención francesa, coma decimal.",
    placeholder: "57,80",
  },
  debt_reference_date: {
    label: "Fecha de corte de la deuda",
    help: "La fecha a la que se refiere el importe («à la date du…»), no la del envío. Formato DD/MM/AAAA.",
    placeholder: "20/07/2026",
  },
  payment_link_label: {
    label: "Etiqueta del enlace de pago",
    help: "El mes y el año que se muestran junto al enlace, en francés.",
    placeholder: "Juillet 2026",
  },
  payment_link: {
    label: "Enlace de pago (Stripe)",
    help: "El hosted_invoice_url por el total adeudado. Pégalo completo, empezando por https://.",
    placeholder: "https://invoice.stripe.com/…",
  },
};

/** Nombre legible de una variable, con fallback al nombre crudo del contrato. */
export function taskVariableLabel(name: string): string {
  return TASK_VARIABLE_HELP[name]?.label ?? name;
}

/**
 * ¿El valor parece un importe en convención FR (coma decimal)? Es un AVISO, no
 * una validación bloqueante: el contrato acepta cualquier string no vacío y el
 * panel no debe impedir un envío legítimo por una heurística. Se limita a
 * detectar el error real y frecuente —punto decimal— para que la operadora lo
 * vea antes de que salga en un acto jurídico.
 */
export function looksLikeFrAmount(value: string): boolean {
  return /^\d{1,3}(?:[\s.]?\d{3})*,\d{2}$/.test(value.trim());
}

/** ¿El valor parece una fecha `DD/MM/AAAA`? Mismo criterio: aviso, no bloqueo. */
export function looksLikeFrDate(value: string): boolean {
  return /^\d{2}\/\d{2}\/\d{4}$/.test(value.trim());
}

/**
 * Aviso de formato para un campo pendiente, o `null` si no hay nada que decir.
 * Los importes y la fecha sólo se comprueban en FR, que es el único mercado con
 * convención propia declarada; la URL se comprueba en todos.
 */
export function taskVariableWarning(
  name: string,
  value: string,
  market: MsOpsMarket,
): string | null {
  if (value.trim() === "") return null;
  if (name.includes("link") && !/^https?:\/\//i.test(value.trim())) {
    return "Se espera una URL completa (https://…). Se enviará tal cual la escribas.";
  }
  if (market !== "FR") return null;
  if (name.endsWith("_amount") && !looksLikeFrAmount(value)) {
    return "Se espera un importe en convención francesa con coma decimal, p. ej. 45,80. Se enviará tal cual lo escribas.";
  }
  if (name === "debt_reference_date" && !looksLikeFrDate(value)) {
    return "Se espera DD/MM/AAAA, p. ej. 20/07/2026. Se enviará tal cual lo escribas.";
  }
  return null;
}


// ---------------------------------------------------------------------------
// Cartera de impagados — `GET /v1/receivables/overview` (2026-08-10).
// Espejo de `ms-ops/packages/contracts/src/opsApi.ts`.
// ---------------------------------------------------------------------------

export type ReceivableBucket =
  | "cadencia_ajustada"
  | "cadencia"
  | "disputado"
  | "write_off"
  | "pendiente";

export type ReceivableRow = {
  leadUlid: string;
  subscriptionRef: string | null;
  episodeId: string | null;
  taskId: string | null;
  currentTemplateId: string | null;
  taskDueAt: string | null;
  bucket: ReceivableBucket;
  /** Céntimos reclamables (saldo del ledger, sin fees). */
  debtCents: number;
  /** Céntimos bloqueados por disputa — NUNCA sumar a la reclamable. */
  blockedCents: number;
  receipts: number;
  oldestExpected: string | null;
  newestExpected: string | null;
  subscriptionStatus: string | null;
};

export type ReceivablesOverviewResponse = {
  market: MsOpsMarket;
  totals: Record<ReceivableBucket, { clients: number; debtCents: number }>;
  rows: ReceivableRow[];
};

export type OpenEpisodeResponse = {
  leadUlid: string;
  episodeId: string;
  subscriptionRef: string;
  created: boolean;
};

export type WriteOffLeadResponse = {
  leadUlid: string;
  receiptsWrittenOff: number;
  centsWrittenOff: number;
  receiptsFailed: string[];
};

/** Panel «Condonados» — traza propia de ms-ops + estado actual del ledger. */
export type WriteOffListItem = {
  id: string;
  leadUlid: string;
  actor: string;
  reason: string;
  receiptsWrittenOff: number;
  centsWrittenOff: number;
  episodeId: string | null;
  createdAt: string;
  restoredAt?: string | null;
  restoredBy?: string | null;
  restoreReason?: string | null;
  /** Recibos que SIGUEN condonados hoy (vacío = restituido o movido). */
  stillWrittenOff: { receiptId: string; status: string; amountCents: number }[];
};

export type WriteOffListResponse = {
  market: MsOpsMarket;
  items: WriteOffListItem[];
};

export type RestoreWriteOffResponse = {
  writeOffId: string;
  receiptsRestored: number;
  centsRestored: number;
  receiptsFailed: string[];
};
